import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

interface EmptyStateProps {
  // Visual: a large icon (FontAwesome / react-icons) above the title
  icon: React.ReactNode;
  title: string;
  // Universal-language description of what this entity is and what it does.
  // Should NOT lean on industry-specific examples — describe the concept.
  description: string;
  // Primary CTA — the "next step" the user should take here.
  primaryAction?: EmptyStateAction;
  // Secondary CTA — alternative path (e.g., "Browse Templates").
  secondaryAction?: EmptyStateAction;
  // "What comes next in the chain" — an optional link out to the upstream
  // or downstream entity. Renders below CTAs as plain text + arrow.
  nextStep?: { label: string; to: string };
  // For use inside an existing card; hides the bordered container.
  bare?: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  nextStep,
  bare = false,
}) => {
  const navigate = useNavigate();
  const inner = (
    <div className="text-center py-12 px-6">
      <div className="text-gray-400 flex justify-center mb-4 text-5xl">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 max-w-md mx-auto mb-6 leading-relaxed">
        {description}
      </p>
      {(primaryAction || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-3 mb-3">
          {primaryAction && (
            <Button variant="primary" onClick={primaryAction.onClick}>
              {primaryAction.icon && <span className="mr-2">{primaryAction.icon}</span>}
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="secondary" onClick={secondaryAction.onClick}>
              {secondaryAction.icon && <span className="mr-2">{secondaryAction.icon}</span>}
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
      {nextStep && (
        <button
          type="button"
          onClick={() => navigate(nextStep.to)}
          className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
        >
          {nextStep.label} →
        </button>
      )}
    </div>
  );
  if (bare) return inner;
  return <div className="bg-white rounded-lg border border-gray-200">{inner}</div>;
};

export default EmptyState;
